import { Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxCurrencyDirective } from 'ngx-currency';
import { CustomersService } from '../../services/customers.service';
import { BALANCE_ENTRY_TYPES, PostedBalanceEntry } from '../../customer.model';

export interface CustomerBalanceEntryDialogData {
  customerId: string;
  customerName: string | null;
  /** Reversing an existing entry rather than posting a new one. */
  reverse?: { ledgerId: string; entryType: string; amount: number; reason: string } | null;
}

/**
 * Post a fee, credit or adjustment straight to a customer's balance — with no invoice and no
 * credit memo behind it — or reverse one already posted.
 *
 * Offered only while the profile's `enableCustomerBalanceEntries` is on and the operator holds
 * `CUSTOMER_BALANCE_ADJUST`; FTE enforces both again and answers 403 either way, so this screen
 * decides what to show, never what is allowed.
 *
 * **The amount is entered as a magnitude and the entry type decides the sign.** FTE takes a
 * signed amount — `+` raises what the customer owes, `−` lowers it — but asking an operator to
 * type a minus sign is asking them to state the direction twice, and two ways of saying one thing
 * can disagree. `ADJ` is the exception, because it is the one type with no fixed direction, so it
 * gets an explicit Charge / Credit choice rather than a sign to remember.
 */
@Component({
  selector: 'app-customer-balance-entry-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './customer-balance-entry-dialog.component.html',
  host: { class: 'component component-customer-balance-entry-dialog' },
})
export class CustomerBalanceEntryDialogComponent {
  private customersService = inject(CustomersService);
  private destroyRef = inject(DestroyRef);

  readonly entryTypes = BALANCE_ENTRY_TYPES;

  entryType: string = 'SVC';
  /** Always positive on screen — `signedAmount` applies the direction. */
  amount = 0;
  /** Only asked for when the type has no fixed direction (`ADJ`). */
  adjustmentDirection: 'charge' | 'credit' = 'charge';
  reason = '';
  reference = '';

  isSaving = false;
  errorMessage: string | null = null;

  /** Post a new entry. */
  static open(
    dialog: MatDialog,
    data: CustomerBalanceEntryDialogData
  ): MatDialogRef<CustomerBalanceEntryDialogComponent, PostedBalanceEntry | undefined> {
    return dialog.open(CustomerBalanceEntryDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      data,
      panelClass: 'customer-balance-entry-dialog',
    });
  }

  constructor(
    public dialogRef: MatDialogRef<CustomerBalanceEntryDialogComponent, PostedBalanceEntry | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerBalanceEntryDialogData
  ) {}

  get isReversal(): boolean {
    return !!this.data.reverse;
  }

  /** The chosen type's rule — which way it moves the balance. */
  get direction(): 'charge' | 'credit' | 'either' {
    return this.entryTypes.find((t) => t.entryType === this.entryType)?.direction ?? 'charge';
  }

  /** Does this type need the operator to say which way it goes? */
  get needsDirection(): boolean {
    return this.direction === 'either';
  }

  /**
   * What FTE is sent. The type fixes the sign for every type but `ADJ`, which asks.
   */
  get signedAmount(): number {
    const magnitude = Math.abs(this.amount || 0);
    const raises = this.direction === 'either' ? this.adjustmentDirection === 'charge' : this.direction === 'charge';
    return raises ? magnitude : -magnitude;
  }

  /** Plain English for what is about to happen, shown under the amount. */
  get effectLabel(): string {
    const signed = this.signedAmount;
    if (!signed) return '';
    return signed > 0 ? 'Raises what the customer owes' : 'Lowers what the customer owes';
  }

  get canSubmit(): boolean {
    if (this.isSaving) return false;
    if (this.isReversal) return this.reason.trim().length > 0;
    return Math.abs(this.amount || 0) > 0.005 && this.reason.trim().length > 0;
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.isSaving = true;
    this.errorMessage = null;

    const request = this.data.reverse
      ? this.customersService.reverseBalanceEntry(this.data.customerId, this.data.reverse.ledgerId, this.reason.trim())
      : this.customersService.postBalanceEntry(this.data.customerId, {
          entryType: this.entryType,
          amount: this.signedAmount,
          reason: this.reason.trim(),
          reference: this.reference.trim() || null,
        });

    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (entry) => this.dialogRef.close(entry),
      error: (error: HttpErrorResponse) => {
        this.isSaving = false;
        // FTE returns its refusals as `{ code, message }` with a real status — a disabled
        // profile is a 403, a bad amount or direction a 400 — so the operator is told which
        // rule they met rather than "something went wrong".
        this.errorMessage =
          error?.error?.message ??
          error?.error?.detail ??
          (error?.status === 403
            ? 'You do not have permission to post to a customer balance, or this profile does not allow it.'
            : 'The entry could not be posted. Please try again.');
      },
    });
  }
}
